import { forwardRef, useCallback, type MouseEvent } from "react";
import {
  Link as RouterLink,
  LinkProps,
  Navigate as RouterNavigate,
  NavigateProps,
  useNavigate as useRouterNavigate,
  useLocation,
  NavigateFunction,
  To,
  NavigateOptions,
} from "react-router-dom";
import { isUnifiedRoute } from "@shared/utils";

/**
 * Патчит путь добавляя /u/ если мы в unified режиме
 */
function patchPath(path: string, isUnified: boolean): string {
  if (!isUnified || !path || path.startsWith("/u/")) {
    return path;
  }

  // Если путь абсолютный и мы в unified режиме
  if (path.startsWith("/projects/")) {
    return `/u${path}`;
  }

  return path;
}

function patchTo(to: To, isUnified: boolean): To {
  if (typeof to === "string") {
    return patchPath(to, isUnified);
  }

  if (to.pathname) {
    return { ...to, pathname: patchPath(to.pathname, isUnified) };
  }

  return to;
}

/**
 * Wrapper для Link компонента с автоматической поддержкой unified режима
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { to, target, onClick, ...rest } = props;
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);

  const patchedTo = patchTo(to, isUnified);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isUnified && target === '_blank' && window.parent !== window) {
      e.preventDefault();
      
      // Используем patchedTo (уже с /u/ префиксом)
      let path = typeof patchedTo === 'string' ? patchedTo : patchedTo.pathname || '';
      
      // Убираем /u/projects/XXX из пути для postMessage
      path = path.replace(/^\/u\/projects\/[^/]+/, '');
      
      window.parent.postMessage({
        type: 'open-in-new-tab',
        path,
      }, '*');
    }
    
    onClick?.(e);
  };

  return <RouterLink ref={ref} to={patchedTo} onClick={handleClick} target={target} {...rest} />;
});

Link.displayName = "Link";

/**
 * Wrapper для Navigate компонента с автоматической поддержкой unified режима
 */
export function Navigate(props: NavigateProps) {
  const { to, ...rest } = props;
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);

  const patchedTo = patchTo(to, isUnified);

  return <RouterNavigate to={patchedTo} {...rest} />;
}

/**
 * Wrapper для useNavigate хука с автоматической поддержкой unified режима
 */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);

  return useCallback<NavigateFunction>(
    (to, options?: NavigateOptions) => {
      if (typeof to === "number") {
        return navigate(to);
      }

      return navigate(patchTo(to, isUnified), options);
    },
    [navigate, isUnified],
  );
}

